import React, { useRef } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import type { XYCoord } from 'react-dnd'
import { GripVertical, X } from 'lucide-react'

interface SortableProductItemProps {
  index: number
  product: { id: number; name: string }
  moveItem: (dragIndex: number, hoverIndex: number) => void
  removeItem: () => void
}

const ItemTypes = {
  PRODUCT: 'product',
}

export function SortableProductItem({ index, product, moveItem, removeItem }: SortableProductItemProps) {
  const ref = useRef<HTMLDivElement>(null)
  
  const [{ handlerId }, drop] = useDrop<
    { index: number },
    void,
    { handlerId: string | symbol | null }
  >({
    accept: ItemTypes.PRODUCT,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      }
    },
    hover(item, monitor) {
      if (!ref.current) return
      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) return
      
      const hoverBoundingRect = ref.current?.getBoundingClientRect()
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const clientOffset = monitor.getClientOffset()
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top
      
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return
      
      moveItem(dragIndex, hoverIndex)
      item.index = hoverIndex
    },
  })

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.PRODUCT,
    item: () => {
      return { index }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  drag(drop(ref))

  return (
    <div ref={ref} data-handler-id={handlerId} className={`flex items-center gap-4 p-3 mb-2 border rounded-md bg-white ${isDragging ? 'opacity-50' : 'opacity-100'}`}>
      <div className="flex items-center cursor-move text-slate-400">
        <GripVertical />
      </div>
      
      <div className="flex-1">
        <p className="text-sm font-medium text-slate-900">{product.name}</p>
      </div>
      
      <button type="button" onClick={removeItem} className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50 transition-colors">
        <X size={16} />
      </button>
    </div>
  )
}
